/**
 * BloodTestBanner Component
 * Displays blood test email status (info, success, error) during booking flow
 */

import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

/**
 * Blood test status banner component
 * @param {Object} props - Component props
 * @param {string} props.status - Status: 'info' | 'sending' | 'success' | 'error'
 * @param {string} props.bloodTestPrice - Price to display
 * @param {string} props.bloodTestUrl - Blood test URL (for copy fallback)
 * @param {string} props.bloodTestType - Type of blood test
 */
const BloodTestBanner = ({
  status = 'info',
  bloodTestPrice = '0',
  bloodTestUrl = '',
  bloodTestType = 'general'
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Get human-readable test type name
  const getTypeName = (type) => {
    const names = {
      hormone: 'Hormone Panel',
      peptide: 'Peptide Panel',
      general: 'General Health Panel'
    };
    return names[type] || 'Blood Test';
  };

  // Handle copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(bloodTestUrl);
      setCopySuccess(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = bloodTestUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
      } catch (e) {
        console.error('Failed to copy link:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  // Info state - before email is sent
  if (status === 'info') {
    return (
      <Alert
        severity="info"
        icon={<EmailIcon />}
        sx={{
          mb: 3,
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>Blood Test Link</AlertTitle>
        After you enter your email, we'll send you the {getTypeName(bloodTestType).toLowerCase()} order link
        (${bloodTestPrice}) so you can complete it at your convenience.
      </Alert>
    );
  }

  // Sending state - email is being sent
  if (status === 'sending') {
    return (
      <Alert
        severity="info"
        icon={<CircularProgress size={20} color="inherit" />}
        sx={{
          mb: 3,
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>Sending Blood Test Link...</AlertTitle>
        We're sending your blood test order link to your email.
      </Alert>
    );
  }

  // Success state - email sent successfully
  if (status === 'success') {
    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon />}
        sx={{
          mb: 3,
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>Blood test link sent to your email!</AlertTitle>
        Check your inbox for the iMedical order link. You can complete the blood test order at your convenience.
      </Alert>
    );
  }

  // Error state - email failed, show copy link fallback
  if (status === 'error') {
    return (
      <>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 3,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>We couldn't send the email</AlertTitle>
          <Box sx={{ mt: 1 }}>
            Don't worry - you can copy your blood test link below. Your booking will continue normally.
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyLink}
              sx={{
                borderColor: 'currentColor',
                '&:hover': {
                  borderColor: 'currentColor',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Copy Blood Test Link
            </Button>
          </Box>
        </Alert>

        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          message="Blood test link copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </>
    );
  }

  // No banner for unknown status
  return null;
};

export default BloodTestBanner;
